<template>
  <div>
    <div v-if="isPresent(activeChallenge)" class="fixed top-0 w-full z-30">
      <ChallengeBanner />
    </div>
    <div
      v-else-if="hasSeatsOrFurniture"
      class="fixed top-0 w-full z-30 cursor-pointer"
      @click="externalPosSubscriptionGuideModalOpen = true"
    >
      <Banner
        :text="$t('external_pos_subscription_guide_banner')"
        bgClass="bg-linear-to-r/srgb from-purple-500 to-purple-600"
        textColorClass="text-white"
        customClasses="border-purple-700"
      />
    </div>
    <TransitionRoot as="template" :show="sidebarOpen">
      <HeadlessUIDialog as="div" class="fixed inset-0 flex z-50 md:hidden" @close="sidebarOpen = false">
        <TransitionChild
          as="template"
          enter="transition-opacity ease-linear duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <DialogOverlay class="fixed inset-0 bg-gray-600/75" />
        </TransitionChild>
        <TransitionChild
          as="template"
          enter="transition ease-in-out duration-300 transform"
          enter-from="-translate-x-full"
          enter-to="translate-x-0"
          leave="transition ease-in-out duration-300 transform"
          leave-from="translate-x-0"
          leave-to="-translate-x-full"
        >
          <div class="relative flex-1 flex flex-col max-w-xs w-full pt-5 pb-4 bg-white">
            <TransitionChild
              as="template"
              enter="ease-in-out duration-300"
              enter-from="opacity-0"
              enter-to="opacity-100"
              leave="ease-in-out duration-300"
              leave-from="opacity-100"
              leave-to="opacity-0"
            >
              <div class="absolute top-0 right-0 -mr-12 pt-2">
                <button
                  type="button"
                  class="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-hidden focus:ring-2 focus:ring-inset focus:ring-white"
                  @click="sidebarOpen = false"
                >
                  <span class="sr-only">Close sidebar</span>
                  <XIcon class="h-6 w-6 text-white" aria-hidden="true" />
                </button>
              </div>
            </TransitionChild>
            <div class="shrink-0 flex items-center justify-between px-4">
              <img class="h-8 w-auto" src="@/assets/large_logo_estaly_black.png" alt="Estaly" />
            </div>
            <div class="mt-5 flex-1 h-0 overflow-y-auto">
              <div v-if="this.currentUserHasPermission('admin')">
                <AdminSelector class="px-2 ml-1 pb-4" @close="sidebarOpen = false" />
              </div>
              <PosMenu @close="sidebarOpen = false" />
            </div>
            <div class="shrink-0 px-4 pb-3 space-y-3">
              <AnnouncementNavButton @click="sidebarOpen = false" />
              <SaturdayChallengeButton
                v-if="saturdayChallengeEnabled && storeRetributionPaymentMethodEstalyGiftCard()"
                @click="
                  saturdayChallengeModalOpen = true;
                  sidebarOpen = false;
                "
              />
              <QuickActionsButton @click="quickActionsModalOpen = true" button-class="w-full" />
            </div>
          </div>
        </TransitionChild>
        <div class="shrink-0 w-14" aria-hidden="true">
          <!-- Dummy element to force sidebar to shrink to fit close icon -->
        </div>
      </HeadlessUIDialog>
    </TransitionRoot>

    <!-- Static sidebar for desktop -->
    <div class="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0" :class="{ 'mt-10': hasVisibleBanner }">
      <!-- Sidebar component, swap this element with another sidebar if you like -->
      <div class="flex flex-col grow pt-5 bg-white overflow-y-auto border-r border-gray-200">
        <div class="flex items-center shrink-0 px-4">
          <img class="h-6 w-auto" src="@/assets/large_logo_estaly_black.png" alt="Estaly" />
        </div>
        <div class="mt-4 flex-1 flex flex-col">
          <div v-if="this.currentUserHasPermission('admin')">
            <AdminSelector class="px-2 ml-1 pb-4" />
          </div>
          <PosMenu />
        </div>
        <div class="shrink-0 px-4 pb-3 space-y-3">
          <AnnouncementNavButton />
          <SaturdayChallengeButton
            v-if="saturdayChallengeEnabled && storeRetributionPaymentMethodEstalyGiftCard()"
            @click="saturdayChallengeModalOpen = true"
          />
          <QuickActionsButton @click="quickActionsModalOpen = true" button-class="w-full" />
        </div>
        <div class="shrink-0 border-t border-gray-200">
          <HeadlessUIMenu as="div" class="relative">
            <MenuButton
              class="w-full min-w-0 flex items-center px-4 py-3 text-left hover:bg-gray-100 group overflow-hidden"
            >
              <div class="flex-1 min-w-0">
                <p class="text-sm font-medium text-gray-900 truncate">{{ currentUser.organization?.name }}</p>
                <p class="text-xs text-gray-500 truncate">{{ currentUser.email }}</p>
              </div>
              <ChevronDownIcon class="h-4 w-4 text-gray-400 shrink-0 ml-2" />
            </MenuButton>
            <transition
              enter-active-class="transition ease-out duration-100"
              enter-from-class="transform opacity-0 scale-95"
              enter-to-class="transform opacity-100 scale-100"
              leave-active-class="transition ease-in duration-75"
              leave-from-class="transform opacity-100 scale-100"
              leave-to-class="transform opacity-0 scale-95"
            >
              <MenuItems
                class="z-50 absolute bottom-full left-0 mb-1 w-full rounded-md shadow-lg py-1 bg-white ring-1 ring-black/5 focus:outline-hidden"
              >
                <MenuItem v-slot="{ active }">
                  <router-link
                    to="/pos/profile"
                    :class="[active ? 'bg-gray-100' : '', 'block px-4 py-2 text-sm text-gray-700']"
                  >
                    {{ $t("Profile") }}
                  </router-link>
                </MenuItem>
                <MenuItem v-slot="{ active }">
                  <span
                    @click="signout"
                    :class="[active ? 'bg-gray-100' : '', 'block px-4 py-2 text-sm text-gray-700']"
                  >
                    {{ $t("Sign out") }}
                  </span>
                </MenuItem>
              </MenuItems>
            </transition>
          </HeadlessUIMenu>
        </div>
      </div>
    </div>
    <div class="md:pl-64 flex flex-col flex-1" :class="{ 'mt-10': hasVisibleBanner }">
      <div
        class="sm:hidden sticky z-20 shrink-0 flex h-16 bg-white shadow-sm"
        :class="hasVisibleBanner ? 'top-10' : 'top-0'"
      >
        <button
          type="button"
          class="px-4border-r border-gray-200 text-gray-500 focus:outline-hidden focus:ring-2 focus:ring-inset focus:ring-primary md:hidden"
          @click="sidebarOpen = true"
        >
          <span class="sr-only">Open sidebar</span>
          <MenuAlt2Icon class="h-6 w-6" aria-hidden="true" />
        </button>
        <div class="flex-1 px-4 flex justify-between">
          <div class="flex-1 flex"></div>
          <div class="ml-4 flex items-center md:ml-6">
            <div class="p-2">
              <a
                class="flex items-center justify-center cursor-pointer"
                target="_blank"
                href="https://estaly.notion.site/FAQ-54e8759929b34ae6a87d05f93abc631e"
              >
                <QuestionMarkCircleIcon class="h-5 w-5 text-gray-500" />
                <p class="ml-1 text-sm text-gray-500">Help</p>
              </a>
            </div>
            <HeadlessUIMenu as="div" class="ml-3 relative">
              <div>
                <MenuButton class="max-w-xs bg-white flex items-center text-sm rounded-full">
                  <span class="sr-only">Open user menu</span>
                  <div
                    class="h-10 w-10 rounded-full bg-primary-lighter hover:bg-primary flex items-center justify-center"
                  >
                    <p class="text-white">{{ currentUser.initials }}</p>
                  </div>
                </MenuButton>
              </div>
              <transition
                enter-active-class="transition ease-out duration-100"
                enter-from-class="transform opacity-0 scale-95"
                enter-to-class="transform opacity-100 scale-100"
                leave-active-class="transition ease-in duration-75"
                leave-from-class="transform opacity-100 scale-100"
                leave-to-class="transform opacity-0 scale-95"
              >
                <MenuItems
                  class="z-50 origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black/5 focus:outline-hidden"
                >
                  <MenuItem v-slot="{ active }">
                    <router-link
                      to="/pos/profile"
                      :class="[active ? 'bg-gray-100' : '', 'block px-4 py-2 text-sm text-gray-700']"
                    >
                      {{ $t("Profile") }}
                    </router-link>
                  </MenuItem>
                  <MenuItem v-slot="{ active }">
                    <span
                      @click="signout"
                      :class="[active ? 'bg-gray-100' : '', 'block px-4 py-2 text-sm text-gray-700']"
                    >
                      {{ $t("Sign out") }}
                    </span>
                  </MenuItem>
                </MenuItems>
              </transition>
            </HeadlessUIMenu>
          </div>
        </div>
      </div>

      <div class="relative min-h-screen">
        <LayoutLoading />
        <main>
          <div class="py-4 pb-24">
            <div class="px-4">
              <router-view></router-view>
            </div>
          </div>
        </main>
      </div>
    </div>

    <!-- Quick Actions Modal -->
    <QuickActionsModal :open="quickActionsModalOpen" @close="quickActionsModalOpen = false" />

    <!-- Saturday Challenge Modal -->
    <SaturdayChallengeModal :open="saturdayChallengeModalOpen" @close="saturdayChallengeModalOpen = false" />

    <!-- External POS Subscription Guide Modal -->
    <ExternalPosSubscriptionGuideModal
      :open="externalPosSubscriptionGuideModalOpen"
      @close="externalPosSubscriptionGuideModalOpen = false"
    />
  </div>
</template>

<script>
import {
  Dialog as HeadlessUIDialog,
  DialogOverlay,
  Menu as HeadlessUIMenu,
  MenuButton,
  MenuItem,
  MenuItems,
  TransitionChild,
  TransitionRoot,
} from "@headlessui/vue";

import { ChevronDownIcon, MenuAlt2Icon, XIcon, QuestionMarkCircleIcon } from "@heroicons/vue/outline";

import { mapActions, mapState, mapGetters } from "vuex";
import AdminSelector from "../components/selector/AdminSelector.vue";
import { initIntercom } from "@/utils/intercom.js";
import { toCamelCase, Banner, isPresent } from "@estaly/ui";
import ChallengesService from "@/api/services/challenges";
import { isSaturdayChallengeEnabled } from "@/utils/saturdayChallenge";

import LayoutLoading from "@/components/utils/LayoutLoading.vue";
import ChallengeBanner from "@/components/challenge/ChallengeBanner.vue";
import SaturdayChallengeButton from "@/components/seller_reward/SaturdayChallengeButton.vue";
import AnnouncementNavButton from "@/components/announcement/AnnouncementNavButton.vue";
import SaturdayChallengeModal from "@/components/seller_reward/SaturdayChallengeModal.vue";
import PosMenu from "../components/navigation/PosMenu.vue";
import QuickActionsModal from "../components/navigation/QuickActionsModal.vue";
import QuickActionsButton from "../components/navigation/QuickActionsButton.vue";
import ExternalPosSubscriptionGuideModal from "../components/pos/ExternalPosSubscriptionGuideModal.vue";

export default {
  components: {
    HeadlessUIDialog,
    DialogOverlay,
    HeadlessUIMenu,
    MenuButton,
    MenuItem,
    MenuItems,
    TransitionChild,
    TransitionRoot,
    ChevronDownIcon,
    MenuAlt2Icon,
    XIcon,
    LayoutLoading,
    QuestionMarkCircleIcon,
    AdminSelector,
    ChallengeBanner,
    PosMenu,
    QuickActionsModal,
    QuickActionsButton,
    SaturdayChallengeButton,
    AnnouncementNavButton,
    SaturdayChallengeModal,
    ExternalPosSubscriptionGuideModal,
    Banner,
  },

  data: () => {
    return {
      sidebarOpen: false,
      activeChallenge: {},
      store: null,
      quickActionsModalOpen: false,
      saturdayChallengeModalOpen: false,
      externalPosSubscriptionGuideModalOpen: false,
    };
  },

  watch: {
    $route: {
      immediate: true,
      handler(to, from) {
        if (to?.path !== from?.path) this.refreshCurrentUser();
      },
    },
    selectedStoreId: {
      immediate: true,
      handler: "handleStoreChange",
    },
  },

  mounted() {
    document.title = "Estaly In Store";
    initIntercom(this.currentUser.intercomUserJwt, this.currentUser.firstName);
    this.retrieveChallengePerformances();
    this.fetchStore();
    this.checkActiveChallenge();
  },

  computed: {
    ...mapState("store", ["selectedStoreId"]),
    ...mapState("auth", ["currentUser"]),
    ...mapGetters("auth", ["currentUserHasPermission", "isSeller", "isResponsible"]),
    ...mapGetters("store", ["storeRetributionPaymentMethodEstalyGiftCard"]),
    ...mapGetters("businessVerticals", ["hasSeatsOrFurniture"]),

    hasVisibleBanner() {
      return isPresent(this.activeChallenge) || this.hasSeatsOrFurniture;
    },

    saturdayChallengeEnabled() {
      return isSaturdayChallengeEnabled();
    },
  },

  methods: {
    ...mapActions("store", { fetchStore: "fetch" }),
    ...mapActions("auth", ["signout", "refreshCurrentUser"]),
    ...mapActions("businessVerticals", ["fetchIndex"]),
    ...mapActions("challenge", ["checkActiveChallenge"]),
    isPresent,

    async fetchBusinessVerticals() {
      if (Number(this.selectedStoreId) > 0) {
        await this.fetchIndex({ storeId: [this.selectedStoreId], enabled: true, includes: ["contract_distribution"] });
      }
    },

    async retrieveChallengePerformances() {
      const response = await ChallengesService.fetchIndex(this.selectedStoreId, { perPage: 2000 });
      if (!response?.success) return;

      const data = toCamelCase(response.data);
      this.activeChallenge = data.challenges.find((challenge) => challenge.active);
    },

    async handleStoreChange(newValue) {
      this.activeChallenge = {};

      if (Number(newValue) > 0) {
        await this.fetchBusinessVerticals();
        await this.retrieveChallengePerformances();
      }
    },
  },
};
</script>
